/**
 * LitPet Doggy Mass Index (DMI)
 * Scoring 1:1 with the previous live tool (wag-data-wonder / PawRx).
 * Progress: localStorage. Leads: Shopify contact + customer forms (+ optional Klaviyo / webhook).
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'litpet:dmi-progress:v1';

  var ZONE_COPY = {
    under: {
      primeLabel: 'Below Ideal',
      benefits: [
        'Building healthy weight supports immunity and muscle tone',
        'Reaching ideal range improves coat condition and energy',
        'Better resilience to illness and recovery from activity'
      ],
      looksLike: [
        'Ribs, spine or hip bones are visible at a glance',
        'Very obvious waist tuck from above and side',
        'Little to no fat cover over the loin or tail base'
      ],
      next: [
        'Increase daily portion by 10–15% for 2 weeks, then reassess',
        'Add a calorie-dense topper or extra meal',
        'Rule out parasites or dental issues with your vet'
      ],
      focusTag: 'Gentle Gain',
      clinical: function (n) {
        return n + ' is below the Ideal zone. Build calories gradually and rule out underlying causes with a vet.';
      }
    },
    lean: {
      primeLabel: 'Near Ideal',
      benefits: [
        'Slight build below ideal still supports joint health',
        'Small increase brings you into the longevity sweet spot',
        'Maintains lean muscle with the right nutrition'
      ],
      looksLike: [
        'Ribs felt easily with only a thin fat layer',
        'Clear waist and tummy tuck',
        'Bones at tail base felt with minimal cover'
      ],
      next: [
        'Nudge portions up by 5–10% and monitor weekly',
        'Prioritise protein-forward meals to preserve muscle',
        'Recheck body condition in 2–4 weeks'
      ],
      focusTag: 'Top Up',
      clinical: function (n) {
        return n + ' is just below the Ideal zone. A small portion increase should restore ideal condition.';
      }
    },
    ideal: {
      primeLabel: 'Ideal Zone',
      benefits: [
        'Dogs in the ideal zone live up to 2.5 years longer on average',
        'Lower risk of arthritis, diabetes and heart disease',
        'Better mobility, stamina and recovery from exercise'
      ],
      looksLike: [
        'Ribs easily felt with a thin, even fat cover',
        'Visible waist when viewed from above',
        'Clear abdominal tuck from the side'
      ],
      next: [
        "Keep current portions and weigh food, don't eyeball",
        'Maintain treats under 10% of daily calories',
        'Recheck DMI every 8–12 weeks to stay on track'
      ],
      focusTag: 'Maintain',
      clinical: function (n) {
        return n + ' is sitting in the Ideal zone. Maintain current feeding and recheck in 8–12 weeks.';
      }
    },
    over: {
      primeLabel: 'Above Ideal',
      benefits: [
        'Losing 5–10% of body weight noticeably improves mobility',
        'Reduces strain on hips, knees and lower back',
        'Lowers risk of inflammation and metabolic disease'
      ],
      looksLike: [
        'Ribs harder to feel under a moderate fat layer',
        'Waist barely visible from above',
        'Abdominal tuck reduced; belly line flattening'
      ],
      next: [
        'Reduce daily portion by 10% and cut high-calorie treats',
        'Swap one meal element for green beans or pumpkin',
        'Add 10 extra minutes of brisk walking per day'
      ],
      focusTag: 'Gentle Trim',
      clinical: function (n) {
        return n + ' is above the Ideal zone. A measured 10% portion reduction and added activity is advised.';
      }
    },
    obese: {
      primeLabel: 'Well Above Ideal',
      benefits: [
        'Even modest weight loss adds healthy years to life',
        'Big reduction in joint pain and breathing effort',
        'Improved insulin sensitivity and energy levels'
      ],
      looksLike: [
        'Ribs difficult or impossible to feel',
        'No waist; back appears broad or rounded',
        'Belly rounds downward with heavy fat deposits'
      ],
      next: [
        'Book a vet check to set a safe weight-loss target',
        'Switch to a measured weight-management formulation',
        'Build activity gradually — short, frequent walks first'
      ],
      focusTag: 'Reset & Restore',
      clinical: function (n) {
        return n + ' is well above the Ideal zone. A vet-supervised weight loss plan is strongly recommended.';
      }
    }
  };

  function defaultState() {
    return {
      unit: 'metric',
      step: 'landing',
      email: '',
      consent: false,
      marketing: false,
      profile: {
        name: '',
        breed: '',
        ageYears: '',
        ageMonths: '',
        sex: '',
        neutered: '',
        bodyType: '',
        activityLevel: '',
        goal: ''
      },
      measurements: {
        weight: '',
        ribcage: '',
        waist: '',
        bodyLength: '',
        shoulderHeight: '',
        feedingPattern: 'set',
        mealsPerDay: '2',
        treatsPerDay: '0-1',
        energyOnPack: '',
        dailyAmount: '',
        dailyAmountUnit: 'g',
        healthFlags: []
      },
      bcs: { ribs: 0, waistTop: 0, tummySide: 0, tailLoin: 0 },
      completed: false,
      savedToShopify: false,
      updatedAt: 0
    };
  }

  function bcsAverage(bcs) {
    var vals = [bcs.ribs, bcs.waistTop, bcs.tummySide, bcs.tailLoin].filter(function (v) {
      return v > 0;
    });
    if (!vals.length) return 0;
    return vals.reduce(function (a, b) { return a + b; }, 0) / vals.length;
  }

  function toKg(weight, unit) {
    var n = parseFloat(weight);
    if (!isFinite(n)) return 0;
    return unit === 'metric' ? n : n * 0.453592;
  }

  function toCm(len, unit) {
    var n = parseFloat(len);
    if (!isFinite(n)) return 0;
    return unit === 'metric' ? n : n * 2.54;
  }

  function computeDmi(state) {
    var e = bcsAverage(state.bcs);
    var rib = toCm(state.measurements.ribcage, state.unit);
    var waist = toCm(state.measurements.waist, state.unit);
    var ratio = rib > 0 && waist > 0 ? waist / rib : 0;

    var score = 50;
    if (e > 0) score = 50 + (e - 3) * 12;
    if (ratio > 0) score += (ratio - 0.87) * 40;
    score = Math.max(0, Math.min(100, Math.round(score)));

    var category = 'unknown';
    if (e > 0) {
      if (e < 2.5) category = 'underweight';
      else if (e <= 3.5) category = 'ideal';
      else if (e <= 4.25) category = 'overweight';
      else category = 'obese';
    }

    var bcs9 = e ? Math.round(((e - 1) / 4) * 8 + 1) : null;

    var zone = 'ideal';
    if (category === 'underweight') zone = 'under';
    else if (category === 'ideal') zone = e < 3 ? 'lean' : 'ideal';
    else if (category === 'overweight') zone = 'over';
    else if (category === 'obese') zone = 'obese';

    var weightKg = toKg(state.measurements.weight, state.unit);
    var recommendation = '';
    if (category === 'underweight') {
      recommendation =
        'Consider increasing daily intake by ~10–15% and re-check in 4 weeks. Pair with vet review if ' +
        weightKg.toFixed(1) +
        ' kg seems low for breed.';
    } else if (category === 'ideal') {
      recommendation = 'Maintain current portions and exercise. Re-check monthly to spot drift early.';
    } else if (category === 'overweight') {
      recommendation =
        'Trim daily intake by ~10% and reduce treats. Add a 10-minute walk daily; re-check in 4 weeks.';
    } else if (category === 'obese') {
      recommendation =
        'Reduce intake by 15–20% and switch to a structured weight-loss plan. Speak with your vet before major changes.';
    }

    var gf = state.profile.goal === 'trim' ? 0.8 : state.profile.goal === 'gain' ? 1.2 : 1;
    var rer = weightKg ? Math.round(70 * Math.pow(weightKg, 0.75)) : null;
    var daily = rer ? Math.round(rer * gf) : null;
    var treats = daily ? Math.round(daily * 0.1) : null;
    var food = daily && treats != null ? daily - treats : null;

    var m = state.measurements;
    var hasMeasures = !!(m.weight && m.ribcage && m.waist);
    var bcsCount = [state.bcs.ribs, state.bcs.waistTop, state.bcs.tummySide, state.bcs.tailLoin].filter(function (v) {
      return v > 0;
    }).length;
    var confidence =
      hasMeasures && bcsCount === 4 ? 'High' : (hasMeasures || bcsCount >= 3) && bcsCount >= 2 ? 'Medium' : 'Low';

    return {
      bcsAvg: e,
      bcs9: bcs9,
      score: score,
      category: category,
      zone: zone,
      waistRibRatio: ratio,
      recommendation: recommendation,
      weightKg: weightKg,
      rer: rer,
      dailyKcal: daily,
      treatBudget: treats,
      foodBudget: food,
      confidence: confidence,
      copy: ZONE_COPY[zone] || ZONE_COPY.ideal
    };
  }

  function loadState() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return defaultState();
      var parsed = JSON.parse(raw);
      var base = defaultState();
      return Object.assign({}, base, parsed, {
        profile: Object.assign({}, base.profile, parsed.profile || {}),
        measurements: Object.assign({}, base.measurements, parsed.measurements || {}),
        bcs: Object.assign({}, base.bcs, parsed.bcs || {})
      });
    } catch (e) {
      return defaultState();
    }
  }

  function saveState(state) {
    state.updatedAt = Date.now();
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) {}
  }

  function clearState() {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (e) {}
  }

  function el(root, sel) {
    return root.querySelector(sel);
  }

  function els(root, sel) {
    return Array.prototype.slice.call(root.querySelectorAll(sel));
  }

  function showPanel(root, name) {
    els(root, '[data-dmi-panel]').forEach(function (p) {
      p.hidden = p.getAttribute('data-dmi-panel') !== name;
    });
  }

  function preferReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function lightHaptic() {
    try {
      if (navigator.vibrate) navigator.vibrate(8);
    } catch (e) {}
  }

  function spawnRipple(target, clientX, clientY) {
    if (!target || preferReducedMotion()) return;
    var rect = target.getBoundingClientRect();
    var size = Math.max(rect.width, rect.height) * 2.2;
    var x = (clientX != null ? clientX : rect.left + rect.width / 2) - rect.left - size / 2;
    var y = (clientY != null ? clientY : rect.top + rect.height / 2) - rect.top - size / 2;
    var ripple = document.createElement('span');
    ripple.className = 'dmi-ripple';
    ripple.style.width = size + 'px';
    ripple.style.height = size + 'px';
    ripple.style.left = x + 'px';
    ripple.style.top = y + 'px';
    target.appendChild(ripple);
    window.setTimeout(function () {
      if (ripple.parentNode) ripple.parentNode.removeChild(ripple);
    }, 560);
  }

  function bindPressFeel(node) {
    if (!node || node.__dmiPress) return;
    node.__dmiPress = true;
    var clear = function () {
      node.classList.remove('is-pressing');
    };
    node.addEventListener(
      'pointerdown',
      function (ev) {
        if (node.disabled) return;
        node.classList.add('is-pressing');
        spawnRipple(node, ev.clientX, ev.clientY);
      },
      { passive: true }
    );
    node.addEventListener('pointerup', clear, { passive: true });
    node.addEventListener('pointercancel', clear, { passive: true });
    node.addEventListener('pointerleave', clear, { passive: true });
    node.addEventListener('blur', clear);
  }

  function moveUnitBlob(root, unit, animate) {
    var switcher = el(root, '[data-dmi-unit-switch]');
    var blob = el(root, '[data-dmi-unit-blob]');
    var active = el(root, '[data-dmi-unit="' + unit + '"]');
    if (!switcher || !blob || !active) return;

    var switchRect = switcher.getBoundingClientRect();
    var btnRect = active.getBoundingClientRect();
    var x = btnRect.left - switchRect.left;
    var w = btnRect.width;

    if (animate && !preferReducedMotion()) {
      switcher.classList.add('is-morphing');
      window.clearTimeout(switcher.__morphTimer);
      switcher.__morphTimer = window.setTimeout(function () {
        switcher.classList.remove('is-morphing');
      }, 220);
    }

    switcher.style.setProperty('--blob-x', x + 'px');
    switcher.style.setProperty('--blob-w', w + 'px');
  }

  function setUnitUI(root, unit, animate) {
    els(root, '[data-dmi-unit]').forEach(function (btn) {
      var active = btn.getAttribute('data-dmi-unit') === unit;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
    els(root, '[data-dmi-unit-label="weight"]').forEach(function (n) {
      n.textContent = unit === 'metric' ? 'kg' : 'lb';
    });
    els(root, '[data-dmi-unit-label="length"]').forEach(function (n) {
      n.textContent = unit === 'metric' ? 'cm' : 'in';
    });
    moveUnitBlob(root, unit, !!animate);
  }

  function fillList(node, items) {
    if (!node) return;
    node.innerHTML = '';
    (items || []).forEach(function (t) {
      var li = document.createElement('li');
      li.textContent = t;
      node.appendChild(li);
    });
  }

  function parseProducts(raw) {
    if (!raw) return [];
    try {
      var list = JSON.parse(raw);
      return Array.isArray(list) ? list.filter(function (p) { return p && p.url; }) : [];
    } catch (e) {
      return [];
    }
  }

  function defaultProducts() {
    return [
      {
        title: 'ImmunoPro – Immunity & Digestion Support Powder',
        url: '/products/immunopro-immunity-digestion-support-powder',
        cartUrl: '/products/immunopro-immunity-digestion-support-powder',
        desc: 'Supports immune function and healthy digestive balance for everyday wellbeing.',
        image: ''
      }
    ];
  }

  function renderProductList(root, products) {
    var list = el(root, '[data-dmi-product-list]');
    if (!list) return;
    var items = products && products.length ? products : defaultProducts();
    list.innerHTML = '';
    items.forEach(function (p) {
      var card = document.createElement('article');
      card.className = 'dmi-product-card';

      if (p.image) {
        var img = document.createElement('img');
        img.className = 'dmi-product-card__img';
        img.src = p.image;
        img.alt = p.title || '';
        img.width = 80;
        img.height = 80;
        img.loading = 'lazy';
        card.appendChild(img);
      }

      var body = document.createElement('div');
      body.className = 'dmi-product-card__body';

      var title = document.createElement('strong');
      title.textContent = p.title || 'Product';
      body.appendChild(title);

      if (p.desc) {
        var desc = document.createElement('p');
        desc.className = 'dmi-lead';
        desc.style.margin = '0';
        desc.textContent = p.desc;
        body.appendChild(desc);
      }

      var actions = document.createElement('div');
      actions.className = 'dmi-product__actions';

      var cart = document.createElement('a');
      cart.className = 'dmi-btn dmi-btn--primary';
      cart.href = p.cartUrl || p.url;
      cart.textContent = 'Add to cart';
      actions.appendChild(cart);

      var view = document.createElement('a');
      view.className = 'dmi-btn dmi-btn--ghost';
      view.href = p.url;
      view.textContent = 'View';
      actions.appendChild(view);

      body.appendChild(actions);
      card.appendChild(body);
      list.appendChild(card);
    });
  }

  function renderResults(root, state, result, cfg) {
    var name = state.profile.name || 'Your dog';
    var copy = result.copy;

    var setText = function (sel, text) {
      var n = el(root, sel);
      if (n) n.textContent = text;
    };

    setText('[data-dmi-result-name]', name);
    setText('[data-dmi-result-zone]', copy.primeLabel);
    setText('[data-dmi-result-focus]', copy.focusTag);
    setText('[data-dmi-result-score]', String(result.score));
    setText('[data-dmi-result-bcs]', result.bcs9 != null ? String(result.bcs9) : '—');
    setText('[data-dmi-result-summary]', result.recommendation);
    setText('[data-dmi-result-clinical]', copy.clinical(name));
    setText('[data-dmi-result-confidence]', result.confidence + ' confidence');
    setText(
      '[data-dmi-result-confidence-note]',
      result.confidence === 'High'
        ? 'Both body check and measurements provided.'
        : result.confidence === 'Medium'
          ? 'Partial inputs — add more details for a sharper read.'
          : 'Limited inputs — results are indicative only.'
    );
    setText('[data-dmi-kcal-daily]', result.dailyKcal != null ? result.dailyKcal + ' kcal/day' : '—');
    setText('[data-dmi-kcal-treat]', result.treatBudget != null ? result.treatBudget + ' kcal/day' : '—');
    setText('[data-dmi-kcal-food]', result.foodBudget != null ? result.foodBudget + ' kcal/day' : '—');

    fillList(el(root, '[data-dmi-benefits]'), copy.benefits);
    fillList(el(root, '[data-dmi-looks]'), copy.looksLike);
    fillList(el(root, '[data-dmi-next]'), copy.next);

    var blurb = el(root, '[data-dmi-product-blurb]');
    if (blurb && cfg.productBlurb) blurb.textContent = cfg.productBlurb;
    renderProductList(root, cfg.products);

    var zoneBar = el(root, '[data-dmi-zone-bar]');
    if (zoneBar) {
      els(zoneBar, '[data-zone]').forEach(function (z) {
        z.classList.toggle('is-active', z.getAttribute('data-zone') === result.zone);
      });
    }
  }

  function buildLeadPayload(state, result, shop) {
    return {
      source: 'litpet-dmi',
      shop: shop || '',
      submitted_at: new Date().toISOString(),
      email: state.email,
      consent: state.consent,
      marketing: state.marketing,
      unit: state.unit,
      profile: state.profile,
      measurements: state.measurements,
      bcs: state.bcs,
      result: {
        score: result.score,
        bcs9: result.bcs9,
        bcs_avg: result.bcsAvg,
        category: result.category,
        zone: result.zone,
        zone_label: result.copy.primeLabel,
        focus: result.copy.focusTag,
        recommendation: result.recommendation,
        clinical: result.copy.clinical(state.profile.name || 'Your dog'),
        daily_kcal: result.dailyKcal,
        treat_budget: result.treatBudget,
        food_budget: result.foodBudget,
        confidence: result.confidence
      },
      metaobject: {
        email: state.email,
        pet_name: state.profile.name || '',
        breed: state.profile.breed || '',
        score: result.score,
        zone: result.zone,
        bcs9: result.bcs9,
        focus: result.copy.focusTag,
        daily_kcal: result.dailyKcal,
        recommendation: result.recommendation,
        submitted_at: new Date().toISOString()
      }
    };
  }

  function postShopifyForm(form) {
    if (!form) return Promise.resolve({ ok: false, reason: 'missing-form' });
    var fd = new FormData(form);
    return fetch(form.getAttribute('action') || '/contact', {
      method: 'POST',
      body: fd,
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    })
      .then(function (res) {
        return { ok: res.ok || res.status === 200 || res.status === 302, status: res.status };
      })
      .catch(function () {
        return { ok: false, reason: 'network' };
      });
  }

  function postKlaviyo(cfg, payload) {
    if (!cfg.klaviyoPublicKey) return Promise.resolve({ ok: false, skipped: true });
    var body = {
      data: {
        type: 'event',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: payload.email,
                properties: {
                  dmi_score: payload.result.score,
                  dmi_zone: payload.result.zone,
                  dmi_bcs9: payload.result.bcs9,
                  dmi_focus: payload.result.focus,
                  dmi_pet_name: payload.profile.name,
                  dmi_breed: payload.profile.breed,
                  dmi_daily_kcal: payload.result.daily_kcal
                }
              }
            }
          },
          metric: { data: { type: 'metric', attributes: { name: 'LitPet DMI Completed' } } },
          properties: payload.result,
          time: payload.submitted_at
        }
      }
    };
    return fetch('https://a.klaviyo.com/client/events/?company_id=' + encodeURIComponent(cfg.klaviyoPublicKey), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        revision: '2024-10-15'
      },
      body: JSON.stringify(body)
    })
      .then(function (res) {
        return { ok: res.ok || res.status === 202 };
      })
      .catch(function () {
        return { ok: false };
      });
  }

  function postWebhook(cfg, payload) {
    if (!cfg.webhookUrl) return Promise.resolve({ ok: false, skipped: true });
    return fetch(cfg.webhookUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(function (res) {
        return { ok: res.ok };
      })
      .catch(function () {
        return { ok: false };
      });
  }

  function fillLeadForms(root, state, result, payload) {
    var contact = el(root, '#LitPetDmiContactForm') || document.getElementById('LitPetDmiContactForm');
    if (contact) {
      var set = function (name, val) {
        var input = contact.querySelector('[name="' + name + '"]');
        if (input) input.value = val == null ? '' : String(val);
      };
      set('contact[email]', state.email);
      set('contact[name]', state.profile.name || 'DMI lead');
      set('contact[subject]', 'LitPet DMI result — ' + (state.profile.name || 'dog') + ' — ' + result.copy.primeLabel);
      set(
        'contact[body]',
        [
          'LitPet DMI submission',
          'Email: ' + state.email,
          'Pet: ' + (state.profile.name || '—') + ' / ' + (state.profile.breed || '—'),
          'Age: ' + (state.profile.ageYears || '—') + 'y ' + (state.profile.ageMonths || '0') + 'm',
          'Goal: ' + (state.profile.goal || '—'),
          'Activity: ' + (state.profile.activityLevel || '—'),
          'Weight: ' + state.measurements.weight + ' ' + (state.unit === 'metric' ? 'kg' : 'lb'),
          'Ribcage / Waist: ' + state.measurements.ribcage + ' / ' + state.measurements.waist,
          'BCS answers: ' +
            state.bcs.ribs +
            ',' +
            state.bcs.waistTop +
            ',' +
            state.bcs.tummySide +
            ',' +
            state.bcs.tailLoin,
          'DMI score: ' + result.score + '/100',
          'Body condition: ' + result.bcs9 + '/9',
          'Zone: ' + result.copy.primeLabel + ' (' + result.zone + ')',
          'Focus: ' + result.copy.focusTag,
          'Daily kcal: ' + (result.dailyKcal || '—'),
          'Recommendation: ' + result.recommendation,
          '',
          'JSON payload:',
          JSON.stringify(payload)
        ].join('\n')
      );
      set('contact[dmi_score]', result.score);
      set('contact[dmi_zone]', result.zone);
      set('contact[dmi_bcs9]', result.bcs9);
      set('contact[dmi_pet_name]', state.profile.name || '');
      set('contact[dmi_focus]', result.copy.focusTag);
      set('contact[enquiry_type]', 'DMI Assessment');
    }

    var customer = el(root, '#LitPetDmiCustomerForm') || document.getElementById('LitPetDmiCustomerForm');
    if (customer) {
      var cset = function (name, val) {
        var input = customer.querySelector('[name="' + name + '"]');
        if (input) input.value = val == null ? '' : String(val);
      };
      cset('contact[email]', state.email);
      cset('contact[tags]', 'dmi-lead, dmi-' + result.zone + (state.marketing ? ', newsletter' : ''));
      var accepts = customer.querySelector('[name="contact[accepts_marketing]"]');
      if (accepts) accepts.checked = !!state.marketing;
    }
  }

  function bindChoiceGroup(root, selector, onPick) {
    els(root, selector).forEach(function (btn) {
      btn.addEventListener('click', function () {
        var group = btn.getAttribute('data-dmi-group');
        els(root, '[data-dmi-group="' + group + '"]').forEach(function (b) {
          b.classList.remove('is-selected');
        });
        btn.classList.add('is-selected');
        lightHaptic();
        onPick(btn.getAttribute('data-value'), btn);
      });
    });
  }

  function syncChoiceUI(root, group, value) {
    els(root, '[data-dmi-group="' + group + '"]').forEach(function (btn) {
      btn.classList.toggle('is-selected', btn.getAttribute('data-value') === String(value));
    });
  }

  function init(root) {
    if (!root || root.__dmiInit) return;
    root.__dmiInit = true;

    var cfg = {
      products: parseProducts(root.getAttribute('data-products')),
      productBlurb: root.getAttribute('data-product-blurb') || '',
      klaviyoPublicKey: root.getAttribute('data-klaviyo-public-key') || '',
      webhookUrl: root.getAttribute('data-webhook-url') || '',
      shop: root.getAttribute('data-shop') || ''
    };
    if (!cfg.products.length) cfg.products = defaultProducts();

    var state = loadState();
    var resultCache = null;

    function persist() {
      saveState(state);
    }

    function go(step) {
      state.step = step;
      persist();
      showPanel(root, step);
      setUnitUI(root, state.unit);
      if (step === 'results') {
        resultCache = computeDmi(state);
        renderResults(root, state, resultCache, cfg);
      }
      root.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    // Press feel on primary controls
    els(root, '.dmi-btn, .dmi-choice, [data-dmi-unit]').forEach(bindPressFeel);

    // Unit toggles — water-drop slide
    els(root, '[data-dmi-unit]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var next = btn.getAttribute('data-dmi-unit');
        if (next === state.unit) {
          moveUnitBlob(root, state.unit, false);
          return;
        }
        state.unit = next;
        persist();
        lightHaptic();
        setUnitUI(root, state.unit, true);
      });
    });

    var onUnitLayout = function () {
      moveUnitBlob(root, state.unit, false);
    };
    window.addEventListener('resize', onUnitLayout, { passive: true });
    if (window.ResizeObserver) {
      var unitSwitch = el(root, '[data-dmi-unit-switch]');
      if (unitSwitch) {
        var ro = new ResizeObserver(onUnitLayout);
        ro.observe(unitSwitch);
      }
    }
    // First paint after fonts/layout settle
    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(onUnitLayout);
    });

    // Landing
    var startFresh = el(root, '[data-dmi-action="start"]');
    if (startFresh) {
      startFresh.addEventListener('click', function () {
        var keepUnit = state.unit;
        state = defaultState();
        state.unit = keepUnit;
        persist();
        go('email');
      });
    }
    var resume = el(root, '[data-dmi-action="resume"]');
    if (resume) {
      resume.addEventListener('click', function () {
        go(state.completed ? 'results' : state.step === 'landing' ? 'email' : state.step);
      });
      var canResume =
        state.step &&
        state.step !== 'landing' &&
        (state.email || state.profile.ageYears || state.measurements.weight);
      resume.hidden = !canResume;
    }

    // Email gate
    var emailInput = el(root, '[data-dmi-email]');
    var consentInput = el(root, '[data-dmi-consent]');
    var marketingInput = el(root, '[data-dmi-marketing]');
    var emailContinue = el(root, '[data-dmi-action="email-continue"]');
    function refreshEmailBtn() {
      if (!emailContinue) return;
      var ok = emailInput && emailInput.value && consentInput && consentInput.checked;
      emailContinue.disabled = !ok;
    }
    if (emailInput) {
      emailInput.value = state.email || '';
      emailInput.addEventListener('input', function () {
        state.email = emailInput.value.trim();
        persist();
        refreshEmailBtn();
      });
    }
    if (consentInput) {
      consentInput.checked = !!state.consent;
      consentInput.addEventListener('change', function () {
        state.consent = consentInput.checked;
        persist();
        refreshEmailBtn();
      });
    }
    if (marketingInput) {
      marketingInput.checked = !!state.marketing;
      marketingInput.addEventListener('change', function () {
        state.marketing = marketingInput.checked;
        persist();
      });
    }
    refreshEmailBtn();
    if (emailContinue) {
      emailContinue.addEventListener('click', function () {
        if (emailContinue.disabled) return;
        state.email = (emailInput && emailInput.value.trim()) || '';
        state.consent = !!(consentInput && consentInput.checked);
        state.marketing = !!(marketingInput && marketingInput.checked);
        persist();
        go('start');
      });
    }

    // Start → profile
    var begin = el(root, '[data-dmi-action="begin"]');
    if (begin) begin.addEventListener('click', function () { go('profile'); });

    // Profile fields
    ['name', 'breed', 'ageYears', 'ageMonths'].forEach(function (key) {
      var input = el(root, '[data-dmi-profile="' + key + '"]');
      if (!input) return;
      input.value = state.profile[key] || '';
      input.addEventListener('change', function () {
        state.profile[key] = input.value;
        persist();
      });
      input.addEventListener('input', function () {
        state.profile[key] = input.value;
        persist();
      });
    });

    bindChoiceGroup(root, '[data-dmi-group="sex"]', function (v) {
      state.profile.sex = v;
      persist();
    });
    bindChoiceGroup(root, '[data-dmi-group="neutered"]', function (v) {
      state.profile.neutered = v;
      persist();
    });
    bindChoiceGroup(root, '[data-dmi-group="bodyType"]', function (v) {
      state.profile.bodyType = v;
      persist();
    });
    bindChoiceGroup(root, '[data-dmi-group="activityLevel"]', function (v) {
      state.profile.activityLevel = v;
      persist();
    });
    bindChoiceGroup(root, '[data-dmi-group="goal"]', function (v) {
      state.profile.goal = v;
      persist();
    });
    syncChoiceUI(root, 'sex', state.profile.sex);
    syncChoiceUI(root, 'neutered', state.profile.neutered);
    syncChoiceUI(root, 'bodyType', state.profile.bodyType);
    syncChoiceUI(root, 'activityLevel', state.profile.activityLevel);
    syncChoiceUI(root, 'goal', state.profile.goal);

    var profileContinue = el(root, '[data-dmi-action="profile-continue"]');
    if (profileContinue) {
      profileContinue.addEventListener('click', function () {
        var p = state.profile;
        if (!p.ageYears || p.ageMonths === '' || !p.bodyType || !p.activityLevel || !p.goal) {
          alert('Please complete age, body type, activity level and goal.');
          return;
        }
        go('measurements');
      });
    }

    // Measurements
    ['weight', 'ribcage', 'waist', 'bodyLength', 'shoulderHeight', 'energyOnPack', 'dailyAmount'].forEach(
      function (key) {
        var input = el(root, '[data-dmi-measure="' + key + '"]');
        if (!input) return;
        input.value = state.measurements[key] || '';
        input.addEventListener('input', function () {
          state.measurements[key] = input.value;
          persist();
        });
      }
    );
    bindChoiceGroup(root, '[data-dmi-group="feedingPattern"]', function (v) {
      state.measurements.feedingPattern = v;
      persist();
    });
    bindChoiceGroup(root, '[data-dmi-group="mealsPerDay"]', function (v) {
      state.measurements.mealsPerDay = v;
      persist();
    });
    bindChoiceGroup(root, '[data-dmi-group="treatsPerDay"]', function (v) {
      state.measurements.treatsPerDay = v;
      persist();
    });
    syncChoiceUI(root, 'feedingPattern', state.measurements.feedingPattern);
    syncChoiceUI(root, 'mealsPerDay', state.measurements.mealsPerDay);
    syncChoiceUI(root, 'treatsPerDay', state.measurements.treatsPerDay);

    els(root, '[data-dmi-flag]').forEach(function (box) {
      var flag = box.getAttribute('data-dmi-flag');
      box.checked = (state.measurements.healthFlags || []).indexOf(flag) !== -1;
      box.addEventListener('change', function () {
        var flags = state.measurements.healthFlags || [];
        if (box.checked) {
          if (flags.indexOf(flag) === -1) flags.push(flag);
        } else {
          flags = flags.filter(function (f) { return f !== flag; });
        }
        state.measurements.healthFlags = flags;
        persist();
      });
    });

    var measureContinue = el(root, '[data-dmi-action="measure-continue"]');
    if (measureContinue) {
      measureContinue.addEventListener('click', function () {
        var m = state.measurements;
        if (!m.weight || !m.ribcage || !m.waist) {
          alert('Please enter weight, ribcage and waist.');
          return;
        }
        go('assessment');
      });
    }

    // BCS
    ['ribs', 'waistTop', 'tummySide', 'tailLoin'].forEach(function (key) {
      bindChoiceGroup(root, '[data-dmi-group="bcs-' + key + '"]', function (v) {
        state.bcs[key] = parseInt(v, 10);
        persist();
      });
      syncChoiceUI(root, 'bcs-' + key, state.bcs[key]);
    });

    var seeResult = el(root, '[data-dmi-action="see-result"]');
    if (seeResult) {
      seeResult.addEventListener('click', function () {
        var b = state.bcs;
        if (!b.ribs || !b.waistTop || !b.tummySide || !b.tailLoin) {
          alert('Please answer all four body-condition questions.');
          return;
        }
        state.completed = true;
        persist();
        go('results');
        saveLeadOnce();
      });
    }

    // Back buttons
    els(root, '[data-dmi-back]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        go(btn.getAttribute('data-dmi-back'));
      });
    });

    var startOver = el(root, '[data-dmi-action="start-over"]');
    if (startOver) {
      startOver.addEventListener('click', function () {
        clearState();
        state = defaultState();
        go('landing');
        if (resume) resume.hidden = true;
      });
    }

    var statusEl = el(root, '[data-dmi-save-status]');

    function saveLeadOnce() {
      if (state.savedToShopify) {
        if (statusEl) statusEl.textContent = 'Result already saved for this session.';
        return;
      }
      resultCache = resultCache || computeDmi(state);
      var payload = buildLeadPayload(state, resultCache, cfg.shop);
      fillLeadForms(root, state, resultCache, payload);
      if (statusEl) statusEl.textContent = 'Saving your result…';

      var contactForm = el(root, '#LitPetDmiContactForm') || document.getElementById('LitPetDmiContactForm');
      var customerForm = el(root, '#LitPetDmiCustomerForm') || document.getElementById('LitPetDmiCustomerForm');

      Promise.all([
        postShopifyForm(contactForm),
        state.marketing || state.consent ? postShopifyForm(customerForm) : Promise.resolve({ skipped: true }),
        postKlaviyo(cfg, payload),
        postWebhook(cfg, payload)
      ]).then(function (outs) {
        var contactOk = outs[0] && outs[0].ok;
        if (contactOk || outs[2].ok || outs[3].ok) {
          state.savedToShopify = true;
          persist();
        }
        if (statusEl) {
          statusEl.textContent = contactOk
            ? 'Saved — we emailed your LitPet team a copy of this result.'
            : 'Result calculated. If save failed, try again or email hello@litpet.co.uk.';
        }
      });
    }

    var resave = el(root, '[data-dmi-action="resave"]');
    if (resave) {
      resave.addEventListener('click', function () {
        state.savedToShopify = false;
        saveLeadOnce();
      });
    }

    // Initial view
    setUnitUI(root, state.unit);
    if (state.completed && state.step === 'results') {
      go('results');
    } else if (state.step && state.step !== 'landing') {
      showPanel(root, state.step);
      setUnitUI(root, state.unit);
    } else {
      showPanel(root, 'landing');
    }
  }

  function boot() {
    els(document, '[data-litpet-dmi]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
